import type { Settings } from "./types";

export const DEFAULT_MEMBER_IDS = [
  "38728add-d970-804c-8be8-e8f7c8d268ac",
  "38728add-d970-810e-a36b-cfee308115d2",
  "38728add-d970-81a3-b478-c5bec4793636",
  "37328add-d970-80a4-834c-dd83379d9b6a",
  "38728add-d970-810a-a43a-c092a9127341",
  "38728add-d970-811e-9ec5-fb40ad93b831",
];

export const DEFAULT_SETTINGS: Settings = {
  memberIds: DEFAULT_MEMBER_IDS,
  from: "2026-08-24",
  to: "2026-09-11",
  minDurationHours: 0.5,
  showUnassignedEvents: true,
  showRawOutlookEvents: false,
  hiddenTeamAddresses: [],
  rainbowEnabled: true,
  mergeEnabled: true,
  showWeekends: false,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isIsoDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [yearText, monthText, dayText] = value.split("-");
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/** 0〜24 時間の範囲に収まる有限値ならその値、そうでなければ undefined。 */
export function durationHours(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 24
    ? value
    : undefined;
}

export function sanitizeSettings(value: unknown, fallback: Settings = DEFAULT_SETTINGS): Settings {
  if (!isRecord(value)) return { ...fallback, memberIds: [...fallback.memberIds] };

  const memberIds = Array.isArray(value.memberIds)
    ? [...new Set(value.memberIds.filter((id): id is string => typeof id === "string" && id.length > 0))]
    : [...fallback.memberIds];
  const from = isIsoDate(value.from) ? value.from : fallback.from;
  const to = isIsoDate(value.to) ? value.to : fallback.to;
  const validRange = from <= to;
  // 最小表示時間は Notion / Outlook で分かれていた。旧設定を読み込んだときに
  // どちらかで見えていた予定が消えないよう、小さいほうへ寄せて1つに統合する。
  const minDurationHours = Math.min(
    durationHours(value.minDurationHours) ?? fallback.minDurationHours,
    durationHours(value.outlookMinDurationHours) ?? Number.POSITIVE_INFINITY,
  );
  const hiddenTeamAddresses = Array.isArray(value.hiddenTeamAddresses)
    ? [
        ...new Set(
          value.hiddenTeamAddresses
            .filter((address): address is string => typeof address === "string")
            .map((address) => address.trim().toLowerCase())
            .filter(Boolean),
        ),
      ]
    : [...fallback.hiddenTeamAddresses];

  return {
    memberIds,
    from: validRange ? from : fallback.from,
    to: validRange ? to : fallback.to,
    minDurationHours,
    showUnassignedEvents:
      typeof value.showUnassignedEvents === "boolean"
        ? value.showUnassignedEvents
        : fallback.showUnassignedEvents,
    showRawOutlookEvents:
      typeof value.showRawOutlookEvents === "boolean"
        ? value.showRawOutlookEvents
        : fallback.showRawOutlookEvents,
    hiddenTeamAddresses,
    rainbowEnabled:
      typeof value.rainbowEnabled === "boolean" ? value.rainbowEnabled : fallback.rainbowEnabled,
    mergeEnabled: typeof value.mergeEnabled === "boolean" ? value.mergeEnabled : fallback.mergeEnabled,
    showWeekends: typeof value.showWeekends === "boolean" ? value.showWeekends : fallback.showWeekends,
  };
}

/**
 * 表示行の並び替え。memberId を toIndex の位置へ移動した新しい配列を返す。
 * 範囲外の toIndex は端に丸め、対象が無い/移動不要のときは同じ内容を返す。
 */
export function moveMemberId(memberIds: string[], memberId: string, toIndex: number): string[] {
  const from = memberIds.indexOf(memberId);
  if (from < 0 || memberIds.length === 0) return [...memberIds];
  const to = Math.max(0, Math.min(toIndex, memberIds.length - 1));
  if (from === to) return [...memberIds];
  const next = [...memberIds];
  next.splice(from, 1);
  next.splice(to, 0, memberId);
  return next;
}

export function settingsFromHash(hash: string): Settings {
  if (hash.length <= 1) return sanitizeSettings(DEFAULT_SETTINGS);
  try {
    return sanitizeSettings(JSON.parse(decodeURIComponent(hash.slice(1))));
  } catch {
    return sanitizeSettings(DEFAULT_SETTINGS);
  }
}

export function settingsHash(settings: Settings): string {
  return `#${encodeURIComponent(JSON.stringify(settings))}`;
}
